import React from 'react';
import SafeIcon from '../../common/SafeIcon';

const CaseStudies = () => {
  const cases = [
    {
      title: "The CQC Turnaround",
      subtitle: "From Requires Improvement to Solid 'Good' with Zero Deficiencies",
      icon: "Shield",
      challenge: "A mid-sized residential care home was facing enforcement action after a poor CQC inspection. Staff morale was low, systems were fragmented, and documentation was chaotic.",
      solution: "Implemented the Kajidori Combined Operating System. Conducted an immediate clinical governance review, instituted daily flash meetings, and rebuilt the QA auditing framework from the ground up.",
      result: "Achieved a 'Good' rating across all domains in under 6 months. Passed local authority AQA auditing with zero deficiencies. Staff retention improved drastically.",
      metrics: ["6 Months turnaround", "Zero AQA Deficiencies", "100% Compliance"]
    },
    {
      title: "The Growth Story",
      subtitle: "Doubling Revenue and Expanding Footprint",
      icon: "TrendingUp",
      challenge: "A successful regional provider hit a growth plateau. They lacked the structural capacity and strategic clarity to scale their operations without compromising care quality.",
      solution: "Provided strategic consulting to restructure the executive board. Developed a scalable operational blueprint and assisted in the tendering process for high-value local authority contracts.",
      result: "Secured £2.5M+ in new contracts. Successfully opened five new specialised facilities within 18 months, doubling annual revenue while maintaining 'Good' CQC ratings.",
      metrics: ["£2.5M+ Contracts Won", "5 New Facilities", "2x Revenue Growth"]
    },
    {
      title: "The Team Builder",
      subtitle: "Solving the Staffing Crisis through Strategic Recruitment",
      icon: "Users",
      challenge: "Severe reliance on expensive agency staff was crippling a care group's finances and disrupting continuity of care for residents.",
      solution: "Overhauled the recruitment and onboarding pipeline. Assisted in obtaining sponsorship licenses and structured an international recruitment drive coupled with robust pastoral support systems.",
      result: "Successfully recruited and integrated 40+ international staff members. Reduced agency spend by 85% and improved overall staff retention by 20% year-on-year.",
      metrics: ["40+ Staff Recruited", "85% Less Agency Spend", "20% Retention Boost"]
    }
  ];

  return (
    <div className="pt-10 pb-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-5xl font-serif font-bold text-navy-900 mb-6">Proven Results.</h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            We don't deal in theory. We deal in evidence. Explore how we have transformed organisations across the UK.
          </p>
        </div>

        <div className="space-y-12">
          {cases.map((study, index) => (
            <div key={index} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300">
              <div className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div className="w-16 h-16 bg-navy-50 rounded-xl flex items-center justify-center shrink-0">
                    <SafeIcon name={study.icon} className="w-8 h-8 text-navy-900" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-2">{study.title}</h2>
                    <p className="text-gold-600 font-medium">{study.subtitle}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">The Challenge</h4>
                      <p className="text-slate-600 leading-relaxed">{study.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">The Solution</h4>
                      <p className="text-slate-600 leading-relaxed">{study.solution}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">The Result</h4>
                      <p className="text-slate-600 leading-relaxed font-medium text-navy-900">{study.result}</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 h-fit">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-navy-900 mb-4">Key Metrics</h4>
                    <ul className="space-y-4">
                      {study.metrics.map((metric, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <SafeIcon name="Check" className="w-5 h-5 text-gold-500" />
                          <span className="font-semibold text-slate-700">{metric}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseStudies;